import type { StepId } from '@store/user/interfaces/userInterfaces';

import type { ApiResponse } from './apiClient';
import { apiClient } from './apiClient';

// Step submission data interfaces
export interface PersonalStepData {
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  nationality: string;
  document_type: string;
  document_number: string;
  passport_expiry?: string;
  passport_emission?: string;
  civil_status: string;
  profession: string;
  occupation: string;
  retired_country?: string;
  fatca_info?: {
    applies: boolean;
    us_citizen?: 'yes' | 'no';
    has_green_card?: 'yes' | 'no';
    stayed_183?: 'yes' | 'no';
    tin?: string;
    tax_country?: string;
  };
}

export interface ResidenceStepData {
  street: string;
  city: string;
  state: string;
  residence: string;
  zip_code: string;
  country: string;
  extra_nationality: {
    applies: boolean;
    nationality_crs?: string;
    tax_residence_country_crs?: string;
    has_taxpayer_number?: 'yes' | 'no';
    taxpayer_number?: string;
    taxpayer_number_reason?: string;
    taxpayer_number_explanation?: string;
  };
}

export interface FinanceStepData {
  monthly_income: number;
  variable_income: boolean;
  countries_of_income: string[];
  deposits: {
    type: string;
    amount: number;
    transactions_quantity: number;
    countries_of_origin: string[];
  };
  withdrawals: {
    type: string;
    amount: number;
    transactions_quantity: number;
    countries_of_origin: string[];
  };
}

export interface JobStepPepInfo {
  is_pep: boolean;
  is_p_e_p?: boolean;
  pep_position?: string;
  pep_institution?: string;
  pep_position_start_date?: string;
  pep_position_end_date?: string;
  family_member_pep: boolean;
  family_member_p_e_p?: boolean;
  family_member_pep_relation?: string;
  family_member_pep_position?: string;
  family_member_pep_date?: string;
}

export interface JobStepData {
  company_name: string;
  initial_deposit_to_open_account: number;
  independent_activity?: string;
  account_type?: string;
  currency?: string;
  pep_info: JobStepPepInfo;
}

export interface ReferenceItemData {
  name: string;
  type: 'familiar' | 'personal' | string;
}

export type ReferencesStepData = ReferenceItemData[];

export interface ValidationStepData {
  document_number: string;
}

export interface ConfirmStepData {
  acceptedTerms: boolean;
  confirmed_at?: string;
}

// API User data interfaces
export interface ApiUserPersonalData {
  passport_expiry: string;
  passport_emission: string;
  full_name: string;
  document_number: string;
  nationality: string;
  phone: string;
  gender: string;
  date_of_birth: string;
  civil_status: string;
  last_name: string;
  second_name?: string;
  first_name: string;
  email: string;
  document_type: string;
  profession?: string;
  profession_code?: string;
  occupation?: string;
  retired_country?: string;
  fatca_info?: {
    applies: boolean;
    us_citizen?: 'yes' | 'no';
    has_green_card?: 'yes' | 'no';
    stayed183?: 'yes' | 'no';
    tin?: string;
    tax_country?: string;
  };
}

export interface ApiUserResidenceData {
  country: string;
  state: string;
  residence: string;
  city: string;
  street: string;
  zip_code?: string;
  extra_nationality?: {
    applies: boolean;
    nationality_crs?: string;
    tax_residence_country_crs?: string;
    has_taxpayer_number?: 'yes' | 'no';
    taxpayer_number?: string;
    taxpayer_number_reason?: string;
    taxpayer_number_explanation?: string;
  };
}

export interface ApiUserReferenceData {
  name: string;
  type: string;
}

// Finance data from the API
export interface ApiUserFinanceData {
  monthly_income: number;
  variable_income: boolean;
  countries_of_income: string[];
  deposits: {
    type: string;
    amount: number;
    transactions_quantity: number;
    countries_of_origin: string[];
  };
  withdrawals: {
    type: string;
    amount: number;
    transactions_quantity: number;
    countries_of_origin: string[];
  };
}

//  Job data from the API
export interface ApiUserJobData {
  company_name: string;
  initial_deposit_to_open_account: number;
  independent_activity?: string;
  account_type?: string;
  currency?: string;
  pep_info: JobStepPepInfo;
}

export interface ApiUserValidationData {
  country?: string;
  is_data_verified: boolean;
  document_number?: string;
  is_biometric_verified?: boolean;
  passport_emission?: string;
  passport_expiry?: string;
  passport_number?: string;
  selfie_url?: string;
  dni?: string;
  document_url?: string;
  is_otp_verified?: boolean;
  otp?: {
    delivery?: string;
    expires_at?: string;
    issue?: number;
    code_hash?: string;
    issued_at?: string;
    verify_count?: number;
    verify_count_date?: string;
    failed_attempts?: number;
    consumed_at?: string;
  };
  policy: {
    accepted: boolean;
    at: string;
    user_agent: string;
    ip: string;
  };
}

export interface ApiOnboardingStep {
  key: string;
  name: string;
  started_at: string;
  finished_at?: string; // Can be undefined
}

export interface ApiOnboardingData {
  application_number?: string;
  current: ApiOnboardingStep;
  steps: ApiOnboardingStep[];
  completed: boolean;
  language: string;
}

// Includes finance and job
export interface ApiUserStepsData {
  personal?: ApiUserPersonalData;
  residence?: ApiUserResidenceData;
  references?: ApiUserReferenceData[];
  finance?: ApiUserFinanceData;
  job?: ApiUserJobData;
  validation?: ApiUserValidationData;
}

export interface ApiUserResponse {
  id: string;
  onboarding: ApiOnboardingData;
  steps: ApiUserStepsData;
}

// Extended response for getUserById that includes token
export interface GetUserByIdResponseData extends ApiUserResponse {
  token: string;
  expires_at: string;
}

export interface GetUserByIdResponse {
  success: boolean;
  data: GetUserByIdResponseData;
}

// Union type for all step data
export type StepData =
  | ValidationStepData
  | PersonalStepData
  | ResidenceStepData
  | FinanceStepData
  | JobStepData
  | ReferencesStepData
  | ConfirmStepData;

// Payload { step, data }
export interface StepSubmissionRequest {
  step: StepId;
  data: StepData;
}

// User read model returned in responses
export interface UserReadModel {
  id: string;
  updated_at: string;
}

export interface StepSubmissionResponse {
  success: boolean;
  data: UserReadModel;
}

// User API service
export class UserApiService {
  /**
   * GET /users/:id - Fetch complete user data by ID
   */
  static async getUserById(
    userId: string,
  ): Promise<ApiResponse<GetUserByIdResponse>> {
    return apiClient.get<GetUserByIdResponse>(`/users/${userId}`);
  }

  /**
   * PATCH /users/:id  -> body: { step, data }
   */
  static async submitStep(
    userId: string,
    step: StepId,
    data: StepData,
  ): Promise<ApiResponse<StepSubmissionResponse>> {
    const payload: StepSubmissionRequest = { step, data };
    return apiClient.patch<StepSubmissionResponse>(`/users/${userId}`, payload);
  }

  /**
   * Submit validation step - now fetches user data instead of submitting DNI
   * */
  static async submitValidationStep(
    userId: string,
  ): Promise<ApiResponse<GetUserByIdResponse>> {
    return this.getUserById(userId);
  }

  /**
   * Submit personal step specifically
   */
  static async submitPersonalStep(
    userId: string,
    data: PersonalStepData,
  ): Promise<ApiResponse<StepSubmissionResponse>> {
    return this.submitStep(userId, 'personal', data);
  }

  /**
   * Submit residence step specifically
   */
  static async submitResidenceStep(
    userId: string,
    data: ResidenceStepData,
  ): Promise<ApiResponse<StepSubmissionResponse>> {
    return this.submitStep(userId, 'residence', data);
  }

  /**
   * Submit finance step specifically
   */
  static async submitFinanceStep(
    userId: string,
    data: FinanceStepData,
  ): Promise<ApiResponse<StepSubmissionResponse>> {
    return this.submitStep(userId, 'finance', data);
  }

  /**
   * Submit job step specifically
   */
  static async submitJobStep(
    userId: string,
    data: JobStepData,
  ): Promise<ApiResponse<StepSubmissionResponse>> {
    return this.submitStep(userId, 'job', data);
  }

  /**
   * Submit references step specifically
   */
  static async submitReferencesStep(
    userId: string,
    data: ReferencesStepData,
  ): Promise<ApiResponse<StepSubmissionResponse>> {
    return this.submitStep(userId, 'references', data);
  }

  /**
   * PUT /users/:id/reset - Reset user onboarding (clears all steps except validation)
   * Reemplaza/vacía todos los steps que el usuario puede volver a ingresar:
   * personal, residence, finance, job, references, confirm.
   * Nunca toca el step validation.
   *
   * @param userId - User ID
   * @returns Fresh user data with only validation step completed
   */
  static async resetUserOnboarding(
    userId: string,
  ): Promise<ApiResponse<GetUserByIdResponse>> {
    return apiClient.put<GetUserByIdResponse>(`/users/${userId}/reset`, {
      reset: true,
    });
  }
}

export default UserApiService;
